import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type RefObject,
} from 'react'

import { useI18N } from '@susu/i18n/client'
import {
  Button,
  Icon,
  InputField,
  Loader,
  SelectField,
} from '@susu/ui/components'

import avo from '../avo/avo'
import { useCustomerData } from '../contexts/CustomerDataContext'
import {
  ROLE,
  useCustomerHub,
  type TrackingContext,
} from '../contexts/CustomerHubContext'
import { useModal } from '../contexts/ModalContext'
import {
  EMPTY_ADDRESS,
  clearFieldError,
  formatAddressLineEntries,
  hasAddressData,
  validateAddress,
  type Address,
} from '../lib/address'
import {
  getClickIdProperties,
  getGAIdentityProperties,
  trackCustomerAddressEdited,
} from '../lib/analytics'
import { COUNTRY_OPTIONS } from '../lib/countries'
import {
  countryUsesStateSelect,
  getSubdivisionOptionsForCountry,
} from '../lib/subdivisions'

import type {
  AddressFieldsValueType,
  CountryValueType,
  CurrencyValueType,
  LanguageValueType,
} from '../avo/avo'
import type { CustomerData } from '../types/customer'

interface AddressBookModalProps {
  customerData: CustomerData
  /** Tracking context for analytics (passed from parent) */
  tracking?: TrackingContext
  onSaveShipping?: (address: Address) => void
  onSaveBilling?: (address: Address) => void
}

type AddressType = 'shipping' | 'billing'
type ViewMode = 'list' | 'edit'

const LISTBOX_MAX_HEIGHT_PX = 188

function useListboxOpenUpward(wrapperRef: RefObject<HTMLDivElement | null>) {
  const [openUpward, setOpenUpward] = useState(false)

  const updateDirection = useCallback(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const trigger = wrapper.querySelector(
      "[role='combobox']",
    ) as HTMLElement | null
    const scrollContainer = wrapper.closest('form') as HTMLElement | null
    if (!trigger || !scrollContainer) return

    const triggerRect = trigger.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    const spaceBelow = containerRect.bottom - triggerRect.bottom
    const spaceAbove = triggerRect.top - containerRect.top

    setOpenUpward(spaceBelow < LISTBOX_MAX_HEIGHT_PX && spaceAbove > spaceBelow)
  }, [wrapperRef])

  useEffect(() => {
    window.addEventListener('resize', updateDirection)
    return () => {
      window.removeEventListener('resize', updateDirection)
    }
  }, [updateDirection])

  return { openUpward, updateDirection }
}

// Address Card Component - matches Figma "Action Card" design
interface AddressCardProps {
  type: AddressType
  address?: Address
  onEdit: () => void
  isActive?: boolean
}

const AddressCard = memo(function AddressCard({
  type,
  address,
  onEdit,
  isActive,
}: AddressCardProps) {
  const { t } = useI18N()
  const hasData = hasAddressData(address)
  // TODO: remove with the v2 address book (#6880, on acceptance), which drops these
  // call sites. Until then these borrow the orderList strings: the addressBook.*Address
  // keys they used were never added to the locale files and rendered as raw keys.
  const title =
    type === 'shipping'
      ? t('orderList.purchaseDetails.shippingAddress')
      : t('orderList.purchaseDetails.billingAddress')

  return (
    <button
      onClick={onEdit}
      className={`flex w-full items-center rounded-lg border p-20 text-left transition-all ${
        isActive
          ? 'scale-[0.99] border-border-secondary bg-surface-hover'
          : 'border-border hover:border-border-hover active:scale-[0.98] active:bg-surface-hover'
      }`}
      type="button"
      aria-label={`Edit ${title.toLowerCase()}`}
      data-testid={`button-edit-${type}-address`}
    >
      <span className="flex flex-1 flex-col gap-2 pb-2 pr-20">
        <span className="text-base font-medium leading-base text-text">
          {title}
        </span>
        {hasData && address ? (
          <span className="flex flex-col text-sm font-light leading-sm text-text">
            {formatAddressLineEntries(address).map(({ key, text }) => (
              <span key={key} data-testid={`address-card-${key}`}>
                {text}
              </span>
            ))}
          </span>
        ) : (
          <span
            className="text-sm font-light leading-sm text-text-secondary"
            data-testid="address-card-empty"
          >
            {t('addressBook.noAddresses')}
          </span>
        )}
      </span>
      <Icon className="-mr-2" name="chevron-right" size={24} color="text" />
    </button>
  )
})

// Address Form Component - Single column layout matching Figma
interface AddressFormProps {
  address: Address
  onChange: (name: string) => React.ChangeEventHandler<HTMLInputElement>
  onCountryChange: (value: string) => void
  onStateChange: (value: string) => void
  errors: Record<string, string>
}

const AddressForm = memo(function AddressForm({
  address,
  onChange,
  onCountryChange,
  onStateChange,
  errors,
}: AddressFormProps) {
  const { t } = useI18N()
  const countrySelectWrapperRef = useRef<HTMLDivElement>(null)
  const stateSelectWrapperRef = useRef<HTMLDivElement>(null)
  const [activeDropdown, setActiveDropdown] = useState<
    'country' | 'state' | null
  >(null)
  const {
    openUpward: openCountryDropdownUpward,
    updateDirection: updateCountryDropdownDirection,
  } = useListboxOpenUpward(countrySelectWrapperRef)
  const {
    openUpward: openStateDropdownUpward,
    updateDirection: updateStateDropdownDirection,
  } = useListboxOpenUpward(stateSelectWrapperRef)

  const showStateSelect = countryUsesStateSelect(address.country)
  const stateOptions = useMemo(
    () => getSubdivisionOptionsForCountry(address.country),
    [address.country],
  )

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const insideCountry = countrySelectWrapperRef.current?.contains(target)
      const insideState = stateSelectWrapperRef.current?.contains(target)
      if (!insideCountry && !insideState) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [])

  return (
    <div className="flex flex-col pt-40 pb-60 gap-20">
      <InputField
        id="address-firstName"
        label={t('addressBook.form.firstName')}
        name="firstName"
        type="text"
        value={address.firstName}
        onChange={onChange('firstName')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.firstName}
        disabled
        data-testid="address-first-name-input"
      />
      <InputField
        id="address-lastName"
        label={t('addressBook.form.lastName')}
        name="lastName"
        type="text"
        value={address.lastName}
        onChange={onChange('lastName')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.lastName}
        disabled
        data-testid="address-last-name-input"
      />
      <InputField
        id="address-street"
        label={t('addressBook.form.addressLine1')}
        name="street"
        type="text"
        value={address.street}
        onChange={onChange('street')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.street}
        data-testid="address-street-input"
      />
      <InputField
        id="address-number"
        label={t('addressBook.form.addressLine2')}
        name="number"
        type="text"
        value={address.number}
        onChange={onChange('number')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.number}
        data-testid="address-number-input"
      />
      <InputField
        id="address-postcode"
        label={t('addressBook.form.postcode')}
        name="postcode"
        type="text"
        value={address.postcode}
        onChange={onChange('postcode')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.postcode}
        data-testid="address-postcode-input"
      />
      <InputField
        id="address-city"
        label={t('addressBook.form.city')}
        name="city"
        type="text"
        value={address.city}
        onChange={onChange('city')}
        onFocus={() => {}}
        onBlur={() => {}}
        error={errors.city}
        data-testid="address-city-input"
      />
      <div
        ref={countrySelectWrapperRef}
        className={`relative ${
          activeDropdown === 'country' ? 'z-[140]' : 'z-[120]'
        } ${
          openCountryDropdownUpward
            ? "[&_[role='listbox']]:top-auto [&_[role='listbox']]:bottom-44"
            : ''
        }`}
        onClick={() => {
          setActiveDropdown('country')
          updateCountryDropdownDirection()
        }}
        onKeyUp={() => {
          setActiveDropdown('country')
          updateCountryDropdownDirection()
        }}
      >
        <SelectField
          id="address-country"
          label={t('addressBook.form.country')}
          name="country"
          value={address.country}
          options={COUNTRY_OPTIONS}
          onChange={onCountryChange}
          error={errors.country}
          placeholder={t('addressBook.form.selectCountry')}
          data-testid="address-country-select"
        />
      </div>
      {showStateSelect ? (
        <div
          ref={stateSelectWrapperRef}
          className={`relative ${
            activeDropdown === 'state' ? 'z-[140]' : 'z-[120]'
          } ${
            openStateDropdownUpward
              ? "[&_[role='listbox']]:top-auto [&_[role='listbox']]:bottom-44"
              : ''
          }`}
          onClick={() => {
            setActiveDropdown('state')
            updateStateDropdownDirection()
          }}
          onKeyUp={() => {
            setActiveDropdown('state')
            updateStateDropdownDirection()
          }}
        >
          <SelectField
            id="address-state"
            label={t('addressBook.form.state')}
            name="state"
            value={address.state}
            options={stateOptions}
            onChange={onStateChange}
            error={errors.state}
            placeholder={
              address.country.trim().toUpperCase() === 'CA'
                ? t('addressBook.form.selectProvince')
                : t('addressBook.form.selectState')
            }
            data-testid="address-state-select"
          />
        </div>
      ) : null}
    </div>
  )
})

/**
 * AddressBookModal Component
 *
 * A modal for viewing and editing shipping and billing addresses.
 *
 * Figma:
 * - List view: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=3199-206480
 * - Edit view: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=3199-210196
 */
export default function AddressBookModal({
  customerData,
  onSaveShipping,
  onSaveBilling,
}: AddressBookModalProps) {
  const { t } = useI18N()
  const { updateCustomer, updateCustomerAsync, isUpdating } = useCustomerData()
  const { setTitle, setOnBack, closeModal } = useModal()
  const { role } = useCustomerHub()
  const [isPending, startTransition] = useTransition()

  const getUserType = () => {
    if (role === ROLE.CUSTOMER) return [avo.UserType.CUSTOMER]
    if (role === ROLE.STYLE_ADVISOR) return [avo.UserType.STYLE_ADVISOR]
    return []
  }

  const getCommonTrackingProps = () => ({
    ...getClickIdProperties(),
    url: window.location.href,
    locationId:
      new URLSearchParams(window.location.search).get('locationId') ?? '',
    userId: customerData.accountNumber,
    loggedIn: avo.LoggedIn.TRUE,
    pageType: avo.PageType.ACCOUNT,
    country: (customerData.countryCode?.toLowerCase() ??
      '') as CountryValueType,
    currency: '' as CurrencyValueType,
    language: (document.documentElement.lang?.split('-')[0] ||
      'en') as LanguageValueType,
    ...getGAIdentityProperties(),
    fbFbp: null,
    fbFbc: null,
    gaAwClid: null,
    eventLabel: null,
    eventCategory: null,
  })

  // Convert CustomerData to Address format
  // Use customer data to populate both billing and shipping (if data exists)
  const hasCustomerAddressData =
    customerData.addressLine1 ||
    customerData.postalCode ||
    customerData.city ||
    customerData.countryCode

  const normalizedStateForCountry = (
    countryCode: string,
    state: string | null | undefined,
  ) => {
    const s = state?.trim() ?? ''
    if (s && countryUsesStateSelect(countryCode)) return s.toUpperCase()
    return s
  }

  const billingAddress: Address | undefined = hasCustomerAddressData
    ? {
        firstName: customerData.firstName || '',
        lastName: customerData.lastName || '',
        street: customerData.addressLine1 || '', // API: addressLine1 → Form: street
        number: customerData.addressLine2 || '', // API: addressLine2 → Form: number
        postcode: customerData.postalCode || '', // API: postalCode → Form: postcode
        city: customerData.city || '',
        state: normalizedStateForCountry(
          customerData.countryCode || '',
          customerData.state,
        ),
        country: customerData.countryCode || '',
      }
    : undefined

  // Initialize shipping with same data as billing if available
  // (customer can modify/differentiate later)
  const shippingAddress: Address | undefined = hasCustomerAddressData
    ? {
        firstName: customerData.firstName || '',
        lastName: customerData.lastName || '',
        street: customerData.addressLine1 || '', // API: addressLine1 → Form: street
        number: customerData.addressLine2 || '', // API: addressLine2 → Form: number
        postcode: customerData.postalCode || '', // API: postalCode → Form: postcode
        city: customerData.city || '',
        state: normalizedStateForCountry(
          customerData.countryCode || '',
          customerData.state,
        ),
        country: customerData.countryCode || '',
      }
    : undefined

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [editingType, setEditingType] = useState<AddressType | null>(null)

  // Form state
  const [formData, setFormData] = useState<Address>(EMPTY_ADDRESS)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  // Handle back to list
  const handleBack = useCallback(() => {
    setOnBack(undefined)
    setTitle(t('modals.addressBook'))
    startTransition(() => {
      setViewMode('list')
      // Clear editing state after transition starts
      setEditingType(null)
      setErrors({})
    })
  }, [setOnBack, setTitle])

  // Handle edit button click
  const handleEdit = useCallback(
    (type: AddressType) => {
      const address = type === 'shipping' ? shippingAddress : billingAddress
      const editTitle =
        type === 'shipping'
          ? t('orderList.purchaseDetails.shippingAddress')
          : t('orderList.purchaseDetails.billingAddress')

      // Update Modal header: show back arrow + edit title
      setOnBack(handleBack)
      setTitle(editTitle)

      // Prefill form with existing address data or use customer data as defaults
      if (address) {
        setFormData(address)
      } else {
        setFormData({
          ...EMPTY_ADDRESS,
          firstName: customerData.firstName || '',
          lastName: customerData.lastName || '',
        })
      }

      setEditingType(type)
      setErrors({})

      startTransition(() => {
        setViewMode('edit')
      })
    },
    [
      shippingAddress,
      billingAddress,
      customerData,
      setOnBack,
      setTitle,
      handleBack,
    ],
  )

  // Stable callbacks for edit buttons (prevents re-renders of memoized AddressCard)
  const handleEditShipping = useCallback(
    () => handleEdit('shipping'),
    [handleEdit],
  )

  // Handle form submission
  const handleSave = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      // Validate form
      const validationErrors = validateAddress(formData, t)
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        return
      }

      setIsSaving(true)
      setErrors({})

      try {
        // Update customer via API and wait for the call to complete
        await updateCustomerAsync({
          firstName: formData.firstName,
          lastName: formData.lastName,
          addressLine1: formData.street, // Form: street → API: addressLine1
          addressLine2: formData.number, // Form: number → API: addressLine2
          postalCode: formData.postcode, // Form: postcode → API: postalCode
          city: formData.city,
          state: formData.state,
          countryCode: formData.country, // Form: country → API: countryCode
        })

        if (editingType) {
          const addressFields: AddressFieldsValueType[] = [
            formData.street && avo.AddressFields.ADDRESS_LINE_1,
            formData.postcode && avo.AddressFields.ZIP_CODE,
            formData.city && avo.AddressFields.CITY,
            formData.country && avo.AddressFields.COUNTRY,
          ].filter(Boolean) as AddressFieldsValueType[]

          trackCustomerAddressEdited({
            ...getCommonTrackingProps(),
            eventLocation: 'customer_hub_settings',
            addressType:
              editingType === 'shipping'
                ? avo.AddressType.SHIPPING
                : avo.AddressType.BILLING,
            addressFields,
            userType: getUserType(),
          })
        }

        // Call optional callbacks if provided
        if (editingType === 'shipping' && onSaveShipping) {
          await onSaveShipping(formData)
        } else if (editingType === 'billing' && onSaveBilling) {
          await onSaveBilling(formData)
        }

        // Close the modal on success so the list view re-renders with fresh data
        // when it is next opened, avoiding showing stale address info.
        closeModal()
      } catch {
        setErrors({ form: t('addressBook.failedToSave') })
      } finally {
        setIsSaving(false)
      }
    },
    [
      formData,
      editingType,
      updateCustomer,
      updateCustomerAsync,
      onSaveShipping,
      onSaveBilling,
      handleBack,
    ],
  )

  // Handle form field change - returns a standard React onChange handler per field name
  const handleFieldChange = useCallback(
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, [name]: value }))
      setErrors((prev) => clearFieldError(prev, name))
    },
    [],
  )

  // Handle country select change (SelectField uses a direct value callback, not a React event)
  const handleCountryChange = useCallback((value: string) => {
    setFormData((prev) => {
      const next = { ...prev, country: value }
      if (!countryUsesStateSelect(value)) {
        next.state = ''
      } else {
        const options = getSubdivisionOptionsForCountry(value)
        const currentValid = options.some((o) => o.value === prev.state)
        if (!currentValid) next.state = ''
      }
      return next
    })
    setErrors((prev) =>
      clearFieldError(clearFieldError(prev, 'country'), 'state'),
    )
  }, [])

  const handleStateChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, state: value }))
    setErrors((prev) => clearFieldError(prev, 'state'))
  }, [])

  // Derive transition states
  const isListView = viewMode === 'list'
  const isEditView = viewMode === 'edit'

  return (
    <div className="flex h-full flex-col pt-20">
      {/* Content Area - only this part slides */}
      <div
        className={`w-full relative flex-1 ${
          isEditView ? 'overflow-visible z-20' : 'overflow-hidden'
        }`}
      >
        {/* List Content */}
        <div
          className={`absolute inset-0 overflow-y-auto pt-20 px-16 pb-60 lg:px-20 3xl:px-120 transition-transform ${
            isListView ? 'translate-x-0' : '-translate-x-full'
          }`}
          aria-hidden={!isListView}
        >
          <div className="flex flex-col gap-12">
            <AddressCard
              type="shipping"
              address={shippingAddress}
              onEdit={handleEditShipping}
              isActive={editingType === 'shipping' && isPending}
            />
          </div>
        </div>

        {/* Edit Content */}
        <form
          className={`absolute inset-0 overflow-y-auto px-16 lg:px-20 3xl:px-120 transition-transform flex flex-col ${
            isEditView ? 'translate-x-0' : 'translate-x-full'
          } ${isEditView ? 'z-20' : ''}`}
          aria-hidden={!isEditView}
          onSubmit={handleSave}
        >
          {errors.form ? (
            <div className="mb-16 rounded-md bg-surface-error p-12">
              <p className="text-sm text-text-error">{errors.form}</p>
            </div>
          ) : null}

          <AddressForm
            address={formData}
            onChange={handleFieldChange}
            onCountryChange={handleCountryChange}
            onStateChange={handleStateChange}
            errors={errors}
          />
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </div>

      {isEditView && (
        <div className="border-t border-border p-16 lg:p-20">
          <Button
            variant="filled"
            size="l"
            onClick={!isSaving && !isUpdating ? handleSave : undefined}
            aria-disabled={isSaving || isUpdating}
            className={`w-full ${isSaving || isUpdating ? 'pointer-events-none opacity-50' : ''}`}
            data-testid="address-form-submit-button"
          >
            {isSaving || isUpdating ? (
              <Loader
                ariaLabel={t('addressBook.savingAddress')}
                className="h-auto"
              />
            ) : (
              t('addressBook.saveAddress')
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
